import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { listTenants, deleteTenant, activateTenant } from '../../api/tenants';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  LocationCity as LocationCityIcon,
  PublishedWithChanges as ActivateIcon,
} from '@mui/icons-material';
import ResponsiveList from '../../components/common/ResponsiveList';

const TenantList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [totalTenants, setTotalTenants] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Fetch tenants
  const fetchTenants = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = { page, page_size: pageSize };
      if (search) {
        params.search = search;
      }

      const data = await listTenants(params);
      setTenants(data.tenants);
      setTotalTenants(data.total);
      setTotalPages(data.total_pages);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError('Failed to load tenants. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTenants();
  }, [page, search]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1); // Reset to first page when search changes
  };

  // Clear search
  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
  };

  // Handle create tenant
  const handleCreateTenant = () => {
    navigate('/admin/tenants/create');
  };

  // Handle edit tenant
  const handleEditTenant = (tenant) => {
    navigate(`/admin/tenants/edit/${tenant.id}`);
  };

  // Promote a shadow FPA. Only ever offered to super admins: the backend
  // scrubs is_shadow to null for everyone else, so `t.is_shadow` is falsy and
  // neither the badge nor this action renders for them.
  const handleActivateTenant = async (tenant) => {
    setIsLoading(true);
    setError(null);
    try {
      await activateTenant(tenant.id);
      fetchTenants();
    } catch (err) {
      console.error('Error activating tenant:', err);
      setError('Failed to activate FPA. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete tenant
  const handleDeleteTenant = (tenant) => {
    setSelectedTenant(tenant);
    setOpenDialog(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    setIsLoading(true);

    try {
      await deleteTenant(selectedTenant.id);
      // Refresh tenant list
      fetchTenants();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      setError('Failed to delete tenant. Please try again later.');
    } finally {
      setIsLoading(false);
      setOpenDialog(false);
    }
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (isLoading && tenants.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Tenant Management
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            label="Search Tenants"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<LocationCityIcon />}
              component={RouterLink}
              to="/admin/suburbs"
            >
              Manage Suburbs
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateTenant}
            >
              Create Tenant
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <ResponsiveList
          items={tenants}
          loading={isLoading}
          empty="No tenants found."
          getKey={(t) => t.id}
          columns={[
            {
              key: 'name',
              header: 'Name',
              render: (t) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {t.name}
                  {t.is_shadow && (
                    <Tooltip title="Record-keeping FPA: no users, no login. Visible to super admins only.">
                      <Chip label="Shadow" size="small" color="warning" variant="outlined" />
                    </Tooltip>
                  )}
                </Box>
              ),
            },
            { key: 'area', header: 'Area', render: (t) => t.area || '-' },
            { key: 'reg', header: 'Registration Number', render: (t) => t.registration_number || '-' },
            { key: 'contact', header: 'Contact Info', render: (t) => t.contact_info || '-' },
            { key: 'created', header: 'Created', render: (t) => new Date(t.created_at).toLocaleDateString() },
            {
              key: 'actions',
              header: 'Actions',
              render: (t) => (
                <>
                  {t.is_shadow && (
                    <Tooltip title="Activate as a live FPA (users still need inviting)">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleActivateTenant(t)}
                        sx={{ mr: 1 }}
                      >
                        <ActivateIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => handleEditTenant(t)} sx={{ mr: 1 }}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDeleteTenant(t)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
          ]}
          mobileCard={(t) => (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                <Box>
                  <IconButton size="small" color="primary" onClick={() => handleEditTenant(t)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteTenant(t)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              {t.area && <Typography variant="body2" color="text.secondary">Area: {t.area}</Typography>}
              {t.registration_number && <Typography variant="body2" color="text.secondary">Reg: {t.registration_number}</Typography>}
              {t.contact_info && <Typography variant="body2" color="text.secondary">{t.contact_info}</Typography>}
              <Typography variant="caption" color="text.secondary">
                Created {new Date(t.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        />

        {tenants.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
              <Typography variant="body2" color="text.secondary" align="center">
                Showing {tenants.length} of {totalTenants} tenants
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete tenant "{selectedTenant?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TenantList;
